import { useEffect } from 'react';

import { useDataTable } from '../../contexts/DataTableContext.js';

/**
 * Finds the open action-menu trigger button inside the anchor row.
 */
function getCellTriggerElement(
  containerElement: HTMLElement,
  rowId: string,
): HTMLElement | null {
  const row = containerElement.querySelector<HTMLElement>(
    `[role="row"][data-rowid="${CSS.escape(rowId)}"]`,
  );
  return row?.querySelector<HTMLElement>('button[data-state="open"]') ?? null;
}

/**
 * Finds the open action-menu trigger button inside the sticky header.
 */
function getHeaderTriggerElement(
  containerElement: HTMLElement,
): HTMLElement | null {
  return containerElement.querySelector<HTMLElement>(
    '[data-rowgroup="header"] button[data-state="open"]',
  );
}

/**
 * Closes an open menu when the table is scrolled far enough that the trigger button the
 * menu is anchored to is no longer visible inside the container. For cell menus all four
 * edges are checked; for header menus only the horizontal edges are checked since the
 * header is sticky and never leaves vertically.
 */
export function useCloseMenuOnScroll(props: {
  isMenuOpen: boolean;
  closeMenu: () => void;
  rowId: string;
  columnId: string;
  isHeader?: boolean;
}): void {
  const { isMenuOpen, closeMenu, rowId, columnId, isHeader = false } = props;
  const table = useDataTable();

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const containerElement = table.getRowVirtualizationContainer();
    if (!containerElement) {
      return;
    }

    const triggerElement = isHeader
      ? getHeaderTriggerElement(containerElement)
      : getCellTriggerElement(containerElement, rowId);

    if (!triggerElement) {
      return;
    }

    // For cell menus shrink the effective root by the sticky header height on the top so
    // triggers behind the header are treated as out of view. Headers are sticky and never
    // leave vertically, so no top adjustment is needed — only the horizontal edges matter.
    const headerHeight = isHeader ? 0 : table.getTotalHeaderHeight();

    // Triggers inside pinned columns are sticky — they never move horizontally, so the
    // IntersectionObserver would fire immediately if horizontal margins were applied.
    // Skip horizontal margins entirely for pinned-column triggers.
    const isColumnPinned = table.getColumn(columnId)?.getIsPinned() !== false;

    // Shrink the horizontal edges by the total width of pinned columns so that a trigger
    // sliding behind a sticky pinned column is treated as out of view.
    // col.getSize() returns TanStack's logical default (150 px) for prefix/suffix columns
    // whose actual rendered width is much smaller — use the measured DOM width instead.
    const leftPinnedWidth = isColumnPinned
      ? 0
      : table
          .getStartLeafColumns()
          .reduce((sum, col) => sum + table.getColumnOrHeaderWidth(col.id), 0);
    const rightPinnedWidth = isColumnPinned
      ? 0
      : table
          .getEndLeafColumns()
          .reduce((sum, col) => sum + table.getColumnOrHeaderWidth(col.id), 0);

    // rootMargin order: top right bottom left
    const rootMargin = [
      `-${headerHeight + 4}px`,
      `-${rightPinnedWidth + 4}px`,
      '-4px',
      `-${leftPinnedWidth + 4}px`,
    ].join(' ');

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry && !entry.isIntersecting) {
          closeMenu();
        }
      },
      {
        root: containerElement,
        rootMargin,
        threshold: 0,
      },
    );

    observer.observe(triggerElement);

    return () => {
      observer.disconnect();
    };
  }, [rowId, columnId, isHeader, closeMenu, isMenuOpen, table]);
}
